import { addDynamicMoney, QuoteItemPriceAdjustment } from './quote-item-price-adjust-view';
import { applyV6PricesToQuoteItemPrice, isEmptyObject } from '../data/v6/helper-functions';
import { customElement } from 'lit/decorators.js';
import { DynamicValueBinder, EventValue, FieldType } from '../../../webmodule-common/other/ui/databinding/data-tracker';
import { EventTemplate } from '../../../webmodule-common/other/ui/events';
import { html } from 'lit';
import { money } from '../../../webmodule-common/other/currency-formatter';

import { QuoteFrameItemPrice } from '../../../webmodule-common/interop/webmodule-interop';
import { QuoteItemPrice, QuotePriceCalculation } from '../../api/dealer-api-interface-quote';
import { QuoteContainerManager, StockLookupViewExtra } from '../data/quote-container';
import { tlang } from '../../../webmodule-common/other/language/lang';
import { pricingStrategies } from '../../../webmodule-common/pricing/pricing-strategies';

@customElement('wm-quoteitempriceadjustmentsupplier')
export class QuoteItemPriceAdjustmentSupplier extends QuoteItemPriceAdjustment {
  private buyInValue: number | null = null;
  private _showExtendedlistPrice: boolean;
  private _showMultiplier: boolean;
  private _showSupplierPriceAdjustment: boolean;

  constructor(
    isClientFacing: boolean,
    quoteManager: QuoteContainerManager,
    quoteItemPrice: QuoteItemPrice,
    quantity: number,
    forceReadonly: boolean,
    buyInCost: StockLookupViewExtra[] | undefined,
    showExtendedListPrice: boolean,
    showMultiplier: boolean,
    showSupplierPriceAdjustment: boolean
  ) {
    super(isClientFacing, quoteManager, quoteItemPrice, quantity, forceReadonly, buyInCost);
    this._showExtendedlistPrice = showExtendedListPrice;
    this._showMultiplier = showMultiplier;
    this._showSupplierPriceAdjustment = showSupplierPriceAdjustment;

    addDynamicMoney(
      this.dataTracker,
      'extendedListPrice',
      () => this.quoteItemPrice.supplierGrossQuantityCost,
      (_value: EventValue) => {
        console.log('extendedListPrice readonly');
      },
      () => true
    );

    // Discount/multiplier = supplierGrossQuantityCost - supplierNettQuantityCost
    // This value is to be "subtracted" from the list price and thus multiplied by -1 to show that it is subtracted
    // This binding is not used, only for display purposes.
    addDynamicMoney(
      this.dataTracker,
      'multiplier',
      () => (this.quoteItemPrice.supplierGrossQuantityCost - this.quoteItemPrice.supplierNettQuantityCost) * -1,
      (_value: EventValue) => {
        console.log('multiplier readonly');
      },
      () => true
    );

    this.dataTracker.addObjectBinding(
      () => this.quoteItemPrice,
      'quantityCost',
      'quantityCost',
      FieldType.money,
      false
    );

    this.dataTracker.addObjectBinding(
      () => this.quoteItemPrice,
      'supplierPriceAdjustment',
      'supplierPriceAdjustment',
      FieldType.money,
      false
    );
    this.dataTracker.addObjectBinding(
      () => this.quoteItemPrice,
      'isTaxableItem',
      'isTaxableItem',
      FieldType.boolean,
      false
    );
  }

  override async afterConstruction(): Promise<void> {
    if (this.buyInCost && this.buyInCost.length > 0) {
      this.buyInValue = this.buyInCost.reduce((total, item) => total + (item.cost ?? 0), 0);
      const field = this.getBuyInFieldName();

      this.dataTracker.addBinding(
        new DynamicValueBinder(
          FieldType.money,
          false,
          () => {
            return this.buyInValue;
          },
          _value => {
            //Read-Only value
          },
          () => true
        ),
        field,
        field,
        FieldType.money,
        false
      );
    }
  }

  getMultiplierLabel(): string {
    return tlang`Multiplier (${(
      this.quoteItemPrice.supplierNettQuantityCost /
      (this.quoteItemPrice.supplierGrossQuantityCost + (this.quoteItemPrice.supplierGrossQuantityCost == 0 ? 1 : 0))
    ).toFixed(4)})`;
  }

  protected eventKeyupSupplierCost = (e: KeyboardEvent) => {
    if (e.code === 'Enter') {
      this.eventChangeSupplierCost();
    }
  };

  protected eventChangeSupplierCost = () => {
    const p = this.quoteItemPrice;
    const newPriceAdjustment = p.quantityCost - (p.supplierNettQuantityCost + (this.buyInValue ?? 0));
    this.processSupplierPriceAdjustment(newPriceAdjustment);
  };
  protected eventKeyupSupplierPriceAdjustment = (e: KeyboardEvent) => {
    if (e.code === 'Enter') {
      this.eventChangeSupplierPriceAdjustment();
    }
  };

  protected eventChangeSupplierPriceAdjustment = () => {
    this.processSupplierPriceAdjustment();
  };

  protected bodyTemplate(): EventTemplate {
    const forms = this.forms;
    const isSupplier = this.claims.isAgent;
    const taxableItemHtml = this.quoteManager.allowNonTaxableItems
      ? forms.switch('isTaxableItem', tlang`Is Taxable Item`)
      : html``;

    const clientFacingQuantity = this.isClientFacing ? forms.intReadonly('quantity', tlang`Quantity`) : html``;
    const supplierSection = this.isClientFacing
      ? undefined
      : html`<h2>${tlang`%%supplier%%`}</h2>
          <div class="form-column">
            ${forms.intReadonly('quantity', tlang`Quantity`)}
            ${this._showExtendedlistPrice || isSupplier
              ? forms.moneyReadonly('extendedListPrice', tlang`Extended List Price`, {
                  toolTip: tlang`The Extended List Price is Quantity multiplied by Item List Price`
                })
              : forms.textHidden('extendedListPrice', tlang`Extended List Price`)}
            ${this._showMultiplier || isSupplier
              ? forms.moneyReadonly('multiplier', this.getMultiplierLabel())
              : forms.textHidden('multiplier', this.getMultiplierLabel())}
            ${!isSupplier
              ? this._showSupplierPriceAdjustment
                ? forms.moneyReadonly('supplierPriceAdjustment', tlang`Price Adjustment`)
                : forms.textHidden('supplierPriceAdjustment', tlang`Price Adjustment`)
              : forms.money('supplierPriceAdjustment', tlang`Price Adjustment`, {
                  events: {
                    blur: this.eventChangeSupplierPriceAdjustment,
                    keyup: this.eventKeyupSupplierPriceAdjustment
                  }
                })}
            ${this.buyInValue ? forms.moneyReadonly(this.getBuyInFieldName(), tlang`Buy-In Items`) : html``}
            ${!isSupplier
              ? forms.moneyReadonly('quantityCost', tlang`Cost Price`)
              : forms.money('quantityCost', tlang`Cost Price`, {
                  events: {
                    blur: this.eventChangeSupplierCost,
                    keyup: this.eventKeyupSupplierCost
                  }
                })}
          </div> `;
    return html`
      <form class="line-item-price-adjustment form-one-col">
        <div class="row">
          ${supplierSection}
          <h2>${tlang`%%franchisee%%`}</h2>
          <div class="form-column">
            ${clientFacingQuantity} ${this.percentMarginOrMarkupTemplate(forms)} ${this.getSubtotalTemplate(forms)}
            ${this.priceAdjustmentTemplate(forms)} ${this.getCalculatedGrossSellingPriceTemplate(forms)}
            ${taxableItemHtml}
          </div>
        </div>
      </form>
    `;
  }

  private processSupplierPriceAdjustment(value?: number) {
    const p = this.quoteItemPrice;
    const frameItemPrice = p.sourceData as unknown as QuoteFrameItemPrice;

    //if value is undefined then the adjustment has
    // already been applied via ui editing
    // if value is assigned, its been calculated
    // after editing quanityCost
    if (value) {
      if (p.supplierPriceAdjustment === value) return;
      p.supplierPriceAdjustment = value;
    }

    if (!isEmptyObject(frameItemPrice)) applyV6PricesToQuoteItemPrice(this.quantity, frameItemPrice, p, this.buyInCost);

    p.calculatedNetSellingPrice = money(
      this.quotePrice.calculation == QuotePriceCalculation.Margin
        ? pricingStrategies.margin.getPriceWithAdjustment(
            p.quantityCost,
            p.percentMarginOrMarkup ?? this.quotePrice.percentMarginOrMarkup,
            undefined
          )
        : pricingStrategies.markup.getPriceWithAdjustment(
            p.quantityCost,
            p.percentMarginOrMarkup ?? this.quotePrice.percentMarginOrMarkup,
            undefined
          ),
      2
    );

    p.calculatedGrossSellingPrice = money(p.calculatedNetSellingPrice + p.priceAdjustment, 2);

    this.requestUpdate();
  }

  private getBuyInFieldName(): string {
    return `taxRate_rate_${this.quoteItemPrice.id}`;
  }
}
